// File: src/ProcessRecipeManager.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, Typography, TextField, Button, MenuItem,
  Stack, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Snackbar,
  Alert, Chip, IconButton, ToggleButtonGroup, ToggleButton, FormControlLabel, Switch,
  Autocomplete, Grid, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';

import {
  getFirestore, collection, doc, setDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, where, serverTimestamp, getDocs
} from 'firebase/firestore';

import { getApp } from 'firebase/app';
import {
  getStorage, ref as storageRef, uploadBytesResumable,
  getDownloadURL, deleteObject
} from 'firebase/storage';

const RECIPE_TYPES = ['cooking', 'mixing', 'prep'];

// Enum options for storage condition (edit to your canonical list)
const STORAGE_CONDITIONS = [
  'Room Temperature (Shelf Stable)',
  'Refrigerated (0–4°C)',
  'Frozen (≤ -18°C)',
  'Airtight Container',
  'Keep Covered',
  'Dry Place',
  'Away from Sunlight',
  'Use Same Day'
];

const cap = (s='') => s.slice(0,1).toUpperCase() + s.slice(1);
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const title = (s='') => cap(s);
const toSlug = (s='') => s.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)+/g,'');
const isFiniteNum = (v) => typeof v === 'number' && Number.isFinite(v);

// helpers
const round1 = (n) => Math.round(n * 10) / 10;
const cToF = (c) => (c * 9) / 5 + 32;
const fToC = (f) => (f - 32) * 5 / 9;

// time helpers
const secsToHMS = (sec) => {
  if (!Number.isFinite(sec)) return '';
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
};
const parseHMS = (input) => {
  if (input == null) return null;
  const str = String(input).trim();
  if (str === '') return null;
  const parts = str.split(':').map(p => p.trim());
  if (parts.some(p => p === '' || !/^\d+$/.test(p))) return NaN;
  if (parts.length === 1) return parseInt(parts[0], 10); // SS
  if (parts.length === 2) { const [mm, ss] = parts.map(Number); if (ss >= 60) return NaN; return mm*60 + ss; }
  if (parts.length === 3) { const [hh, mm, ss] = parts.map(Number); if (mm >= 60 || ss >= 60) return NaN; return hh*3600 + mm*60 + ss; }
  return NaN;
};
const omitEmpty = (obj) => {
  const out = {};
  for (const [k,v] of Object.entries(obj || {})) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'object' && !Array.isArray(v)) {
      const child = omitEmpty(v);
      if (Object.keys(child).length === 0) continue;
      out[k] = child; continue;
    }
    out[k] = v;
  }
  return out;
};

/* ------------------ Add/Edit Dialog ------------------ */
function RecipeDialog({
  open,
  onClose,
  initial,
  defaultType='mixing',
  lockType=false,
  units,
  ingredients,
  tools
}) {
  const editing = !!initial;

  // Storage instance
  const storage = React.useMemo(() => {
    try { return getStorage(getApp()); } catch { return getStorage(); }
  }, []);

  // Core fields
  const [name, setName] = useState(initial?.name || '');
  const [recipeType, setRecipeType] = useState(initial?.recipe_type || defaultType);
  const [yieldAmount, setYieldAmount] = useState(initial?.yield?.amount ?? '');
  const [yieldUnitId, setYieldUnitId] = useState(initial?.yield?.unit_id ?? '');
  const [equipmentIds, setEquipmentIds] = useState(asArray(initial?.equipment_ids));

  // Ingredients
  const emptyIng = { ingredient_id:'', qty:'', unit_id:'', note:'' };
  const [rows, setRows] = useState(() => {
    if (initial?.ingredients?.length) return initial.ingredients;
    const type = (initial?.recipe_type || defaultType);
    return type === 'prep' ? [] : [emptyIng];
  });

  // Steps (with images + UI-only fields)
  const emptyStep = {
    text:'', note:'',
    timer:{ label:'', seconds:'' },
    timer_str:'',
    temperature_c:'', temperature_f:'',
    images: [],
    _imagesMeta: [],    // { url, path }
    _uploads: {}        // { path: pct }
  };
  const [steps, setSteps] = useState(() => {
    if (initial?.steps?.length) {
      return initial.steps.map((s) => {
        const c = Number(s.temperature_c);
        const f = isFiniteNum(c) ? round1(cToF(c)) : '';
        const secs = Number(s?.timer?.seconds);
        const timer_str = isFiniteNum(secs) && secs > 0 ? secsToHMS(secs) : '';
        return {
          ...emptyStep,
          ...s,
          temperature_f: f,
          timer_str,
          images: Array.isArray(s.images) ? s.images : []
        };
      });
    }
    return [];
  });

  // Storage + notes
  const [storageInfo, setStorageInfo] = useState(initial?.storage || { shelf_life_days: '', condition: '' });
  const [notes, setNotes] = useState(initial?.notes || '');

  // Whether condition is a custom string not in enum
  const conditionIsCustom = useMemo(
    () => !!storageInfo.condition && !STORAGE_CONDITIONS.includes(storageInfo.condition),
    [storageInfo.condition]
  );
  const [useCustomCondition, setUseCustomCondition] = useState(conditionIsCustom);

  // Snack
  const [snack, setSnack] = useState({ open:false, severity:'info', message:'' });

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setRecipeType(initial?.recipe_type || defaultType);
    setYieldAmount(initial?.yield?.amount ?? '');
    setYieldUnitId(initial?.yield?.unit_id ?? '');
    setEquipmentIds(asArray(initial?.equipment_ids));
    if (initial?.steps?.length) {
      setSteps(initial.steps.map((s) => {
        const c = Number(s.temperature_c);
        const f = isFiniteNum(c) ? round1(cToF(c)) : '';
        const secs = Number(s?.timer?.seconds);
        const timer_str = isFiniteNum(secs) && secs > 0 ? secsToHMS(secs) : '';
        return { ...emptyStep, ...s, temperature_f: f, timer_str, images: Array.isArray(s.images) ? s.images : [] };
      }));
    } else {
      setSteps([]);
    }
    const type = (initial?.recipe_type || defaultType);
    setRows(initial?.ingredients?.length ? initial.ingredients : (type === 'prep' ? [] : [emptyIng]));
    setStorageInfo(initial?.storage || { shelf_life_days:'', condition:'' });
    // reset custom condition toggle based on incoming value
    const isCustom = !!(initial?.storage?.condition) && !STORAGE_CONDITIONS.includes(initial.storage.condition);
    setUseCustomCondition(isCustom);
    setNotes(initial?.notes || '');
  }, [open, initial, defaultType]);

  /* -------- filter lookups -------- */
  const filteredIngredients = useMemo(() => {
    const rt = title(recipeType);
    return ingredients.filter(ing => {
      const tps = asArray(ing.types?.length ? ing.types : (ing.type ? [ing.type] : []));
      return tps.includes(rt) || tps.includes('Beverage');
    });
  }, [ingredients, recipeType]);

  const selectedIngredientObjs = useMemo(() => {
    return rows.map(r => ingredients.find(i => i.id === r.ingredient_id)).filter(Boolean);
  }, [rows, ingredients]);

  const ingredientOptionsUnion = useMemo(() => {
    const map = new Map();
    filteredIngredients.forEach(i => map.set(i.id, i));
    selectedIngredientObjs.forEach(i => map.set(i.id, i));
    return Array.from(map.values());
  }, [filteredIngredients, selectedIngredientObjs]);

  const filteredTools = useMemo(() => {
    const rt = title(recipeType);
    return tools.filter(t => {
      if ((t.status || 'active') !== 'active') return false;
      const tps = asArray(t.types);
      if (tps.length === 0 && t.category) {
        return String(t.category).toLowerCase() === String(recipeType).toLowerCase();
      }
      return tps.includes(rt) || tps.includes('Beverage');
    });
  }, [tools, recipeType]);

  const selectedTools = useMemo(() => equipmentIds.map(id => tools.find(t => t.id === id)).filter(Boolean), [equipmentIds, tools]);

  /* ---------------- array helpers ---------------- */
  const addRow   = () => setRows(prev => [...prev, { ...emptyIng }]);
  const rmRow    = (idx) => setRows(prev => prev.filter((_,i)=>i!==idx));
  const upRow    = (idx, patch) => setRows(prev => prev.map((r,i)=> i===idx ? { ...r, ...patch } : r));

  const addStep  = () => setSteps(prev => [...prev, { ...emptyStep }]);
  const rmStep   = (idx) => setSteps(prev => prev.filter((_,i)=>i!==idx));
  const upStep   = (idx, patch) => setSteps(prev => prev.map((r,i)=> i===idx ? { ...r, ...patch } : r));

  // Temp & timer sync
  const onTempFChange = (idx, valStr) => {
    setSteps(prev => prev.map((s,i) => {
      if (i !== idx) return s;
      const f = Number(valStr);
      if (!Number.isFinite(f)) return { ...s, temperature_f: valStr, temperature_c: '' };
      const c = round1(fToC(f));
      return { ...s, temperature_f: valStr, temperature_c: c };
    }));
  };
  const onTempCChange = (idx, valStr) => {
    setSteps(prev => prev.map((s,i) => {
      if (i !== idx) return s;
      const c = Number(valStr);
      if (!Number.isFinite(c)) return { ...s, temperature_c: valStr, temperature_f: '' };
      const f = round1(cToF(c));
      return { ...s, temperature_c: valStr, temperature_f: f };
    }));
  };
  const onTimerStrChange = (idx, str) => {
    setSteps(prev => prev.map((s,i) => {
      if (i !== idx) return s;
      const parsed = parseHMS(str);
      const seconds = parsed == null || Number.isNaN(parsed) ? '' : parsed;
      return { ...s, timer_str: str, timer: { ...(s.timer || {}), seconds } };
    }));
  };

  /* ---------------- step images ---------------- */
  const addStepImages = (stepIdx, fileList) => {
    const slug = toSlug(name) || 'unsaved';
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setSnack({ open:true, severity:'info', message:`Uploading ${files.length} file(s)…` });

    // Ensure a container for uploads exists
    setSteps(prev => prev.map((s,i) => i===stepIdx ? { ...s, _uploads: { ...s._uploads } } : s));

    files.forEach((file) => {
      const safeName = file.name.replace(/[^\w.\-]+/g, '_');
      const path = `process_recipes/${slug}/steps/${stepIdx}/${Date.now()}-${safeName}`;
      const ref = storageRef(storage, path);
      const metadata = { contentType: file.type || 'image/*' };
      const task = uploadBytesResumable(ref, file, metadata);

      // initialize progress
      setSteps(prev => prev.map((s,i) => (i!==stepIdx) ? s : { ...s, _uploads: { ...s._uploads, [path]: 0 } }));

      task.on('state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setSteps(prev => prev.map((s,i) => (i!==stepIdx) ? s : { ...s, _uploads: { ...s._uploads, [path]: pct } }));
        },
        (err) => {
          console.error('upload error', err);
          setSnack({ open:true, severity:'error', message:`Upload failed: ${err?.message || err}` });
          // remove failed entry
          setSteps(prev => prev.map((s,i) => {
            if (i !== stepIdx) return s;
            const { [path]: _ignore, ...rest } = s._uploads || {};
            return { ...s, _uploads: rest };
          }));
        },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            setSteps(prev => prev.map((s,i) => {
              if (i !== stepIdx) return s;
              const nextImgs = [...(s.images || []), url];
              const nextMeta = [...(s._imagesMeta || []), { url, path }];
              const { [path]: _ignore, ...rest } = s._uploads || {};
              return { ...s, images: nextImgs, _imagesMeta: nextMeta, _uploads: rest };
            }));
            setSnack({ open:true, severity:'success', message:'Image uploaded.' });
          } catch (e) {
            console.error('downloadURL error', e);
            setSnack({ open:true, severity:'error', message:`Could not fetch URL: ${e?.message || e}` });
          }
        }
      );
    });
  };

  const removeStepImage = async (stepIdx, url) => {
    const meta = steps[stepIdx]?._imagesMeta?.find(m => m.url === url);
    if (meta?.path) {
      try { await deleteObject(storageRef(storage, meta.path)); }
      catch (e) { console.warn('deleteObject failed:', e?.message); }
    }
    setSteps(prev => prev.map((s,i) => {
      if (i !== stepIdx) return s;
      return {
        ...s,
        images: (s.images || []).filter(u => u !== url),
        _imagesMeta: (s._imagesMeta || []).filter(m => m.url !== url)
      };
    }));
    setSnack({ open:true, severity:'info', message:'Image removed.' });
  };

  /* ---------------- validation ---------------- */
  const requireBatch = recipeType === 'cooking' || recipeType === 'mixing';

  const errors = [];
  if (!name.trim()) errors.push('Name is required.');
  if (requireBatch && (!yieldAmount || !yieldUnitId)) {
    errors.push('Batch size (amount + unit) is required for Cooking and Mixing.');
  }

  // Validate only filled ingredient rows (allow empty list, esp. for Prep)
  const filledRows = rows.filter(r =>
    r.ingredient_id || r.qty !== '' || r.unit_id || (r.note && r.note.trim() !== '')
  );
  const badIngredient = filledRows.some(r =>
    !r.ingredient_id || r.qty === '' || Number(r.qty) <= 0 || !r.unit_id
  );
  if (badIngredient) errors.push('Each filled ingredient row needs ingredient, qty > 0, and unit.');

  const firstBadTimer = steps.findIndex(s => s.timer_str && Number.isNaN(parseHMS(s.timer_str)));
  if (firstBadTimer >= 0) errors.push(`Timer invalid at step ${firstBadTimer + 1} (use HH:MM:SS, MM:SS, or SS).`);

  const id = initial?.id || toSlug(name);
  const canSave = errors.length === 0 && id;

  /* ---------------- save normalize ---------------- */
  const onSave = () => {
    const normIngredients = filledRows
      .map(r => omitEmpty({
        ingredient_id: r.ingredient_id,
        qty: Number(r.qty),
        unit_id: r.unit_id,
        note: (r.note || '').trim() || undefined
      }))
      .filter(r => r.ingredient_id && isFiniteNum(r.qty) && r.qty > 0 && r.unit_id);

    const normSteps = steps
      .map(s => {
        const parsed = parseHMS(s.timer_str);
        const seconds = parsed == null || Number.isNaN(parsed) ? undefined : parsed;
        const cNum = Number(s?.temperature_c);
        const temperature_c = isFiniteNum(cNum) ? cNum : undefined;
        return omitEmpty({
          text: (s.text || '').trim(),
          note: (s.note || '').trim() || undefined,
          timer: seconds ? omitEmpty({ label: (s?.timer?.label || '').trim() || undefined, seconds }) : undefined,
          temperature_c,
          images: Array.isArray(s.images) && s.images.length ? s.images : undefined
        });
      })
      .filter(s => (s.text || '').trim());

    const storageObj = {};
    const sh = Number(storageInfo?.shelf_life_days);
    if (isFiniteNum(sh)) storageObj.shelf_life_days = sh;

    // write either enum value or custom string
    const cond = (useCustomCondition ? storageInfo.condition : storageInfo.condition)?.trim();
    if (cond) storageObj.condition = cond;

    const payload = omitEmpty({
      id,
      name: name.trim(),
      recipe_type: recipeType,
      archived: false,
      equipment_ids: asArray(equipmentIds),
      yield: (yieldAmount && yieldUnitId)
        ? { amount: Number(yieldAmount), unit_id: yieldUnitId }
        : undefined,
      ingredients: normIngredients,
      steps: normSteps,
      storage: Object.keys(storageObj).length ? storageObj : undefined,
      notes: notes.trim() || undefined,
      v: '2.3.1'
    });

    onClose(payload);
  };

  /* ---------------- render ---------------- */
  return (
    <Dialog open={open} onClose={()=>onClose(null)} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Edit ${cap(recipeType)} Recipe` : `Add ${cap(recipeType)} Recipe`}</DialogTitle>
      <DialogContent dividers>
        {errors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning" variant="outlined">
              {errors[0]}
            </Alert>
          </Box>
        )}

        <Stack gap={2}>
          {!lockType && (
            <ToggleButtonGroup exclusive size="small" color="primary" value={recipeType} onChange={(_,v)=> v && setRecipeType(v)}>
              {RECIPE_TYPES.map(rt => <ToggleButton key={rt} value={rt}>{cap(rt)}</ToggleButton>)}
            </ToggleButtonGroup>
          )}

          <TextField label="Recipe name" value={name} onChange={e=>setName(e.target.value)} fullWidth />

          <Stack direction={{ xs:'column', sm:'row' }} gap={2}>
            <TextField
              label="Batch amount"
              type="number"
              value={yieldAmount}
              onChange={e=>setYieldAmount(e.target.value)}
              fullWidth
              helperText={requireBatch ? 'Required for Cooking/Mixing' : 'Optional for Prep'}
              required={requireBatch}
            />
            <TextField
              label="Batch unit"
              select
              value={yieldUnitId}
              onChange={e=>setYieldUnitId(e.target.value)}
              fullWidth
              helperText={requireBatch ? 'Required for Cooking/Mixing' : 'Optional for Prep'}
              required={requireBatch}
            >
              {units.map(u => <MenuItem key={u.id} value={u.id}>{u.name || u.id}</MenuItem>)}
            </TextField>
          </Stack>

          {/* Equipment */}
          <Autocomplete
            multiple
            options={tools.filter(t => (t.status || 'active') === 'active')}
            getOptionLabel={(o)=>o?.name || ''}
            value={selectedTools}
            onChange={(_, val)=> setEquipmentIds(val.map(v=>v.id))}
            renderInput={(params)=><TextField {...params} label="Equipment / Tools" placeholder="Select tools"/>}
          />

          <Divider />

          {/* Ingredients */}
          <Stack gap={1}>
            <Typography variant="subtitle1">Ingredients</Typography>
            {rows.map((r,idx)=> {
              const ingObj = ingredientOptionsUnion.find(i => i.id === r.ingredient_id) || null;
              return (
                <Stack key={idx} direction={{ xs:'column', sm:'row' }} gap={1} alignItems="flex-start">
                  <Autocomplete
                    options={ingredientOptionsUnion}
                    value={ingObj}
                    onChange={(_, val)=>upRow(idx, { ingredient_id: val?.id || '' })}
                    getOptionLabel={(o)=>o?.name || ''}
                    renderInput={(params)=><TextField {...params} label={`Ingredient ${idx+1}`} sx={{ minWidth: 260 }}/>}
                  />
                  <TextField label="Qty" type="number" value={r.qty} onChange={e=>upRow(idx, { qty: e.target.value })} sx={{ width: 120 }} />
                  <TextField label="Unit" select value={r.unit_id} onChange={e=>upRow(idx, { unit_id: e.target.value })} sx={{ minWidth: 180 }}>
                    {units.map(u => <MenuItem key={u.id} value={u.id}>{u.name || u.id}</MenuItem>)}
                  </TextField>
                  <IconButton aria-label="remove" onClick={()=>rmRow(idx)} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Stack>
              );
            })}
            <Button variant="outlined" onClick={addRow} startIcon={<AddIcon/>} sx={{ alignSelf:'flex-start' }}>Add ingredient</Button>
          </Stack>

          <Divider />

          {/* Steps */}
          <Stack gap={1}>
            <Typography variant="subtitle1">Steps</Typography>
            {steps.map((s,idx)=> (
              <Box key={idx} sx={{ p: 1.5, border: '1px solid #eee', borderRadius: 2 }}>
                <Stack gap={1}>
                  <TextField label={`Step ${idx+1} text`} value={s.text} onChange={e=>upStep(idx, { text: e.target.value })} fullWidth />
                  <TextField label="Note (optional)" value={s.note || ''} onChange={e=>upStep(idx, { note: e.target.value })} fullWidth />
                  <Stack direction={{ xs:'column', sm:'row' }} gap={1}>
                    <TextField label="Timer (HH:MM:SS)" value={s.timer_str} onChange={e=>onTimerStrChange(idx, e.target.value)} />
                    <TextField label="Temp °C" value={s.temperature_c} onChange={e=>onTempCChange(idx, e.target.value)} />
                    <TextField label="Temp °F" value={s.temperature_f} onChange={e=>onTempFChange(idx, e.target.value)} />
                  </Stack>

                  {/* Image uploader */}
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Button variant="outlined" startIcon={<ImageIcon/>} component="label">
                      Add images
                      <input hidden type="file" accept="image/*" multiple onChange={(e)=> { addStepImages(idx, e.target.files); e.target.value=''; }} />
                    </Button>
                    {/* Upload progress */}
                    {Object.entries(s._uploads || {}).map(([path, pct]) => (
                      <Stack key={path} direction="row" alignItems="center" gap={1} sx={{ minWidth: 220 }}>
                        <Typography variant="caption" sx={{ width: 120, overflow:'hidden', textOverflow:'ellipsis' }}>{path.split('/').pop()}</Typography>
                        <LinearProgress variant="determinate" value={Number(pct) || 0} sx={{ flex: 1, minWidth: 100 }} />
                        <Typography variant="caption">{pct}%</Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {/* Thumbnails */}
                  {Array.isArray(s.images) && s.images.length > 0 && (
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      {s.images.map((url) => (
                        <Grid key={url} item>
                          <Box sx={{ width: 96, height: 96, borderRadius: 1, overflow:'hidden', border:'1px solid #eee', position:'relative' }}>
                            <img src={url} alt="step" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                            <IconButton size="small" sx={{ position:'absolute', top: -8, right: -8, bgcolor: 'white' }} onClick={()=>removeStepImage(idx, url)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  <Stack direction="row" gap={1}>
                    <Button size="small" color="error" onClick={()=>rmStep(idx)}>Remove step</Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
            <Button variant="outlined" onClick={addStep} startIcon={<AddIcon/>} sx={{ alignSelf:'flex-start' }}>Add step</Button>
          </Stack>

          <Divider />

          {/* Storage + Notes */}
          <Stack direction={{ xs:'column', sm:'row' }} gap={2}>
            <TextField
              label="Shelf life (days)"
              type="number"
              value={storageInfo.shelf_life_days}
              onChange={e=>setStorageInfo(prev=>({ ...prev, shelf_life_days: e.target.value }))}
            />

            {/* Enum with "Other…" option */}
            <TextField
              label="Storage condition"
              select
              value={useCustomCondition ? 'custom' : (storageInfo.condition || '')}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'custom') {
                  setUseCustomCondition(true);
                } else {
                  setUseCustomCondition(false);
                  setStorageInfo(prev => ({ ...prev, condition: v }));
                }
              }}
              fullWidth
              helperText="Pick a standard condition or choose Other…"
            >
              {STORAGE_CONDITIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
              <MenuItem value="custom">Other…</MenuItem>
            </TextField>
          </Stack>

          {useCustomCondition && (
            <TextField
              label="Custom storage condition"
              value={storageInfo.condition || ''}
              onChange={e=>setStorageInfo(prev=>({ ...prev, condition: e.target.value }))}
              fullWidth
              placeholder="e.g., Vacuum-sealed, Nitrogen-flushed, etc."
            />
          )}

          <TextField label="Notes" multiline minRows={3} value={notes} onChange={e=>setNotes(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={()=>onClose(null)}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------ Manager (list page) ------------------ */
export default function ProcessRecipeManager({ defaultType='mixing', lockType=false }) {
  const db = getFirestore();

  // lookups
  const [units, setUnits] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [tools, setTools] = useState([]);

  // recipes
  const [recipeType, setRecipeType] = useState(defaultType);
  const [recipes, setRecipes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [qText, setQText] = useState('');

  // sync chip/button with the current tab
  useEffect(() => { setRecipeType(defaultType); }, [defaultType]);

  // ui
  const [dialog, setDialog] = useState({ open:false, initial:null });
  const [confirmArchive, setConfirmArchive] = useState({ open:false, row:null });
  const [confirmRestore, setConfirmRestore] = useState({ open:false, row:null });
  const [confirmDelete, setConfirmDelete] = useState({ open:false, row:null });
  const [snack, setSnack] = useState({ open:false, severity:'success', message:'' });

  // load lookups
  useEffect(() => {
    (async () => {
      const unitSnap = await getDocs(collection(db, 'ingredient_units'));
      setUnits(unitSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const ingSnap = await getDocs(collection(db, 'recipe_ingredients'));
      const ingRows = ingSnap.docs.map(d => {
        const data = d.data() || {};
        const types = asArray(data.types?.length ? data.types : (data.type ? [data.type] : []));
        return { id: d.id, ...data, types };
      });
      setIngredients(ingRows);

      const qTools = query(collection(db, 'equipment_tools'), orderBy('name'));
      const unsubTools = onSnapshot(qTools, snap => {
        setTools(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      });
      return () => unsubTools();
    })();
  }, [db]);

  // subscribe recipes by type
  useEffect(() => {
    const col = collection(db, 'process_recipes');
    const qy = query(col, where('recipe_type', '==', recipeType));
    const unsub = onSnapshot(qy, snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRecipes(rows);
    });
    return () => unsub();
  }, [db, recipeType]);

  const listFiltered = React.useMemo(() => {
    const rows = showArchived ? recipes : recipes.filter(r => !r.archived);
    const t = qText.trim().toLowerCase();
    const filtered = t
      ? rows.filter(r =>
          (r.name || '').toLowerCase().includes(t) ||
          (r.notes || '').toLowerCase().includes(t)
        )
      : rows;
    return filtered.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [recipes, showArchived, qText]);

  // actions
  const upsert = async (payloadRaw) => {
    const now = serverTimestamp();
    const payload = {
      ...payloadRaw,
      recipe_type: payloadRaw.recipe_type || recipeType,
      updated_at: now,
      created_at: payloadRaw.created_at || now
    };
    await setDoc(doc(collection(db, 'process_recipes'), payload.id), payload, { merge: true });
    setSnack({ open:true, severity:'success', message:'Recipe saved.' });
  };

  const doArchive = async (row) => {
    await updateDoc(doc(collection(db, 'process_recipes'), row.id), { archived: true, updated_at: serverTimestamp() });
    setSnack({ open:true, severity:'info', message:'Recipe archived.' });
  };
  const doRestore = async (row) => {
    await updateDoc(doc(collection(db, 'process_recipes'), row.id), { archived: false, updated_at: serverTimestamp() });
    setSnack({ open:true, severity:'success', message:'Recipe restored.' });
  };
  const doDelete = async (row) => {
    await deleteDoc(doc(collection(db, 'process_recipes'), row.id));
    setSnack({ open:true, severity:'warning', message:'Recipe deleted.' });
  };

  return (
    <Box>
      <Card className="rounded-2xl shadow">
        <CardHeader
          title={
            <Stack direction={{ xs:'column', md:'row' }} alignItems={{ xs:'flex-start', md:'center' }} justifyContent="space-between" gap={2}>
              <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="h5">Process Recipes</Typography>
                <Chip label={`Type: ${cap(recipeType)}`} color="primary" />
              </Stack>
              <Stack direction="row" gap={1} alignItems="center">
                {!lockType && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={recipeType}
                    onChange={(_, v)=> v && setRecipeType(v)}
                  >
                    {RECIPE_TYPES.map(rt => <ToggleButton key={rt} value={rt}>{cap(rt)}</ToggleButton>)}
                  </ToggleButtonGroup>
                )}
                <Button startIcon={<AddIcon/>} variant="contained" onClick={()=> setDialog({ open:true, initial:null })}>
                  Add {cap(recipeType)} Recipe
                </Button>
              </Stack>
            </Stack>
          }
          subheader="Create and manage Cooking, Mixing, and Prep recipes with a unified schema."
        />
        <CardContent>
          <Stack direction={{ xs:'column', md:'row' }} gap={2} alignItems={{ xs:'stretch', md:'center' }}>
            <TextField label="Search recipes" value={qText} onChange={(e)=>setQText(e.target.value)} fullWidth />
            <FormControlLabel control={<Switch checked={showArchived} onChange={e=>setShowArchived(e.target.checked)} />} label="Show Archived" />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack gap={1}>
            {listFiltered.map(row => {
              const batch = row.yield ? `${row.yield.amount} ${row.yield.unit_id}` : '—';
              return (
                <Card key={row.id} variant="outlined" className="rounded-xl">
                  <CardContent>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={7}>
                        <Typography variant="h6">{row.name}</Typography>
                        <Typography variant="body2">ID: {row.id}</Typography>
                        <Typography variant="body2">Batch: {batch}</Typography>
                        {row.equipment_ids?.length > 0 && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Equipment: {row.equipment_ids.join(', ')}
                          </Typography>
                        )}
                        {row.notes && (
                          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                            Notes: {row.notes}
                          </Typography>
                        )}
                        {row.archived && <Chip size="small" color="warning" label="Archived" sx={{ mt: 0.5 }} />}
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Stack direction="row" justifyContent={{ xs:'flex-start', md:'flex-end' }} gap={1}>
                          <Button size="small" startIcon={<EditIcon/>} onClick={()=> setDialog({ open:true, initial: row })}>
                            Edit
                          </Button>
                          {!row.archived ? (
                            <Button size="small" color="warning" startIcon={<ArchiveIcon/>} onClick={()=> setConfirmArchive({ open:true, row })}>
                              Archive
                            </Button>
                          ) : (
                            <Button size="small" startIcon={<RestoreIcon/>} onClick={()=> setConfirmRestore({ open:true, row })}>
                              Restore
                            </Button>
                          )}
                          <Button size="small" color="error" startIcon={<DeleteIcon/>} onClick={()=> setConfirmDelete({ open:true, row })}>
                            Delete
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
            {listFiltered.length === 0 && (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>No recipes found for this view.</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <RecipeDialog
        open={dialog.open}
        initial={dialog.initial}
        defaultType={recipeType}
        lockType={lockType}
        units={units}
        ingredients={ingredients}
        tools={tools}
        onClose={async (payload) => {
          setDialog({ open:false, initial:null });
          if (!payload) return;
          await upsert(payload);
        }}
      />

      {/* Confirmations */}
      <Dialog open={confirmArchive.open} onClose={()=>setConfirmArchive({ open:false, row:null })}>
        <DialogTitle>Archive recipe?</DialogTitle>
        <DialogContent>
          <Typography>This will hide <strong>{confirmArchive.row?.name}</strong> from active lists. You can restore it later.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmArchive({ open:false, row:null })}>Cancel</Button>
          <Button color="warning" onClick={async ()=>{
            await doArchive(confirmArchive.row);
            setConfirmArchive({ open:false, row:null });
          }}>Archive</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRestore.open} onClose={()=>setConfirmRestore({ open:false, row:null })}>
        <DialogTitle>Restore recipe?</DialogTitle>
        <DialogContent>
          <Typography>Make <strong>{confirmRestore.row?.name}</strong> active again.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmRestore({ open:false, row:null })}>Cancel</Button>
          <Button onClick={async ()=>{
            await doRestore(confirmRestore.row);
            setConfirmRestore({ open:false, row:null });
          }}>Restore</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete.open} onClose={()=>setConfirmDelete({ open:false, row:null })}>
        <DialogTitle>Delete permanently?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete <em>{confirmDelete.row?.name}</em>. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDelete({ open:false, row:null })}>Cancel</Button>
          <Button color="error" onClick={async ()=>{
            await doDelete(confirmDelete.row);
            setConfirmDelete({ open:false, row:null });
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack(s => ({ ...s, open:false }))}>
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
